
import { defineComponent, ref, Ref, UnwrapRef, PropType, reactive, computed, toRefs, ComputedRef } from "vue";
import { Sensor } from "@/interfaces/Sensor";
import { Burst, SingleBurst } from "@/interfaces/Burst";
import { fetchBursts, fetchSensors, fetchTaskRules } from "@/helpers/api";
import Datepicker from "vue3-datepicker";
import { Temporal, toTemporalInstant } from "@js-temporal/polyfill";
import { TaskRule } from "@/interfaces/TaskRule";
import { getDateFormat, getSelectedBursts } from "@/helpers/store";
import { jsTimeToRealEpoch, getNextDayFromDate, timeAsJS } from "@/helpers/time";
import { fetchBurstDataArray, groupBursts } from "@/helpers/helpers";
import { plot } from "@/helpers/plot";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import { Batch } from "@/interfaces/Batch";
import BatchItem from "@/components/BatchItem.vue";

declare global {
  interface Date {
    toTemporalInstant(this: Date): Temporal.Instant;
  }
}

Date.prototype.toTemporalInstant = toTemporalInstant;

interface BurstsData {
  selectedBursts: Ref<Burst[]>;
  batches: Batch[];
  filters: {
    site: string;
    aggregator: string;
    node_name: string;
    sensor_name: string;
    batch_id: string;
    task_rule_id: string;
    burst_limit: string;
  };
  sensors: Sensor[];
  task_rules: TaskRule[];
  pickerStartDate: Ref<UnwrapRef<Date>>;
  pickerEndDate: Ref<UnwrapRef<Date>>;
  chart: null;
  showGraphModal: boolean;
  dateFormat: "dd/MM/yyyy HH:mm" | "MM/dd/yyyy HH:mm";
  isLoading: boolean;
  sites: ComputedRef<Set<string>>;
  aggregators: ComputedRef<Set<string>>;
  node_names: ComputedRef<Set<string>>;
  sensor_names: ComputedRef<Set<string>>;
  errorMessage: string | null;
}

export default defineComponent({
  components: {
    Datepicker,
    LoadingSpinner,
    BatchItem,
  },
  props: {
    startDate: {
      required: false,
      default: new Date(new Date().setDate(new Date().getDate() - 1)).toISOString(),
      type: String as PropType<string>,
    },
    endDate: {
      required: false,
      default: new Date().toISOString(),
      type: String as PropType<string>,
    },
    dateSet: {
      required: false,
      default: false,
      type: Boolean as PropType<boolean>,
    },
  },
  setup(props) {
    const state = reactive({
      selectedBursts: getSelectedBursts(),
      batches: [],
      filters: {
        site: "",
        aggregator: "",
        node_name: "",
        sensor_name: "",
        batch_id: "",
        task_rule_id: "null",
        burst_limit: "1000",
      },
      sensors: [],
      task_rules: [],
      pickerStartDate: ref(timeAsJS(props.startDate)),
      pickerEndDate: ref(timeAsJS(props.endDate)),
      chart: null,
      showGraphModal: false,
      dateFormat: getDateFormat(),
      isLoading: true,
      sites: computed(() => {
        const filteredSensors: Sensor[] = state.sensors.filter((sensor: Sensor) =>
          !sensor.Tags ||
          !sensor.Tags.Site ||
          (state.filters.aggregator && sensor.Tags.Aggregator != state.filters.aggregator) ||
          (state.filters.node_name && sensor.Tags.NodeName != state.filters.node_name) ||
          (state.filters.sensor_name && sensor.Name != state.filters.sensor_name)
            ? false
            : true
        );
        return new Set(filteredSensors.map((sensor) => sensor.Tags.Site).sort());
      }),
      aggregators: computed(() => {
        const filteredSensors: Sensor[] = state.sensors.filter((sensor: Sensor) =>
          !sensor.Tags ||
          !sensor.Tags.Aggregator ||
          (state.filters.site && sensor.Tags.Site != state.filters.site) ||
          (state.filters.node_name && sensor.Tags.NodeName != state.filters.node_name) ||
          (state.filters.sensor_name && sensor.Name != state.filters.sensor_name)
            ? false
            : true
        );
        return new Set(filteredSensors.map((sensor) => sensor.Tags.Aggregator).sort());
      }),
      node_names: computed(() => {
        const filteredSensors: Sensor[] = state.sensors.filter((sensor: Sensor) =>
          !sensor.Tags ||
          !sensor.Tags.NodeName ||
          (state.filters.site && sensor.Tags.Site != state.filters.site) ||
          (state.filters.aggregator && sensor.Tags.Aggregator != state.filters.aggregator) ||
          (state.filters.sensor_name && sensor.Name != state.filters.sensor_name)
            ? false
            : true
        );
        return new Set(filteredSensors.map((sensor) => sensor.Tags.NodeName).sort());
      }),
      sensor_names: computed(() => {
        const filteredSensors: Sensor[] = state.sensors.filter((sensor: Sensor) =>
          (state.filters.site && sensor.Tags.Site != state.filters.site) ||
          (state.filters.aggregator && (!sensor.Tags || sensor.Tags.Aggregator != state.filters.aggregator)) ||
          (state.filters.node_name && (!sensor.Tags || sensor.Tags.NodeName != state.filters.node_name))
            ? false
            : true
        );
        return new Set(filteredSensors.map((sensor) => sensor.Name).sort());
      }),
      errorMessage: null as string | null,
    } as BurstsData);

    const flatBursts: ComputedRef<Burst[]> = computed(() => state.batches.map((batch: Batch) => batch.Bursts).flat());

    const bursts_selected = () => (state.selectedBursts.length === 0 ? false : true);

    const setToggleError = (error: Error) => {
      state.errorMessage = `Error Toggling all bursts: ${error.message ? error.message : ""}`;
    };

    const get_sensors = async () => {
      const sensors = await fetchSensors();
      state.sensors = sensors;
    };

    const get_task_rules = async () => {
      const taskRules = await fetchTaskRules();
      state.task_rules = taskRules;
    };

    const get_bursts = async (event: Event | null = null) => {
      state.isLoading = true;
      // Build the query string based on the current filters.
      // The start and end dates are not in the Vue model, because the datepicker doesn't
      // fire the correct events to update the Vue model when it changes the dates - so
      // instead we just read them straight from the form fields.
      const params = new URLSearchParams();
      if (state.pickerStartDate) {
        params.set("StartTime", jsTimeToRealEpoch(state.pickerStartDate));
      }
      if (state.pickerEndDate) {
        params.set("EndTime", jsTimeToRealEpoch(state.pickerEndDate));
      }
      if (state.filters.site) {
        params.set("SensorTags.Site", state.filters.site);
      }
      if (state.filters.aggregator) {
        params.set("SensorTags.Aggregator", state.filters.aggregator);
      }
      if (state.filters.node_name) {
        params.set("SensorTags.NodeName", state.filters.node_name);
      }
      if (state.filters.sensor_name) {
        params.set("SensorName", state.filters.sensor_name);
      }
      if (state.filters.batch_id) {
        params.set("BatchID", state.filters.batch_id);
        params.set("StartTime", "0");
      }
      if (state.filters.burst_limit) {
        params.set("Limit", state.filters.burst_limit);
      } else {
        params.set("Limit", "1000");
      }
      if (state.filters.task_rule_id) {
        params.set("TaskRuleID", state.filters.task_rule_id);
      }

      const rawBursts = await fetchBursts(event || props.dateSet ? params : new URLSearchParams());
      const processedBursts = rawBursts.map((burst) => {
        burst.Duration = (burst.NSamples / burst.SampleRate).toFixed(0) + "s";
        return burst;
      });
      state.batches = [...new Set(processedBursts.map((burst) => burst.BatchID))].map((batchNumber) => {
        const batchBurstList = processedBursts.filter((burst) => burst.BatchID === batchNumber);
        return {
          BatchID: batchNumber,
          BatchName: batchBurstList[0].BatchName,
          BatchTags: batchBurstList[0].BatchTags,
          Bursts: batchBurstList,
          StartTime: batchBurstList.reduce(
            (prev, cur) => (Temporal.Instant.compare(prev.StartTime, cur.StartTime) ? cur : prev),
            {
              StartTime: Temporal.Now.zonedDateTimeISO("Europe/London").toString(),
            }
          ).StartTime,
        };
      });
      state.isLoading = false;
    };

    const burst_limit_enforce_range = () => {
      if (Number(state.filters.burst_limit) > 1000) {
        state.filters.burst_limit = "1000";
      }
      if (Number(state.filters.burst_limit) < 1) {
        state.filters.burst_limit = "1";
      }
    };

    const plotGraph = async (burst: SingleBurst) => {
      plot(groupBursts(await fetchBurstDataArray([burst]))[0]).then(() => (state.showGraphModal = true));
    };

    const burstOnPageSelected = () =>
      state.batches
        .map((batch) => batch.Bursts)
        .flat()
        .filter((burst) =>
          state.selectedBursts.filter((selectedBurst) => burst.BurstID === selectedBurst.BurstID).length > 0
            ? true
            : false
        ).length > 0
        ? true
        : false;

    const push_end_date = () => {
      if (state.pickerEndDate) {
        if (state.pickerStartDate > state.pickerEndDate) {
          state.pickerEndDate = getNextDayFromDate(state.pickerStartDate);
        }
      }
    };

    get_sensors();
    get_task_rules();
    get_bursts();

    return {
      ...toRefs(state),
      bursts_selected,
      plotGraph,
      get_bursts,
      burst_limit_enforce_range,
      burstOnPageSelected,
      setToggleError,
      push_end_date,
      flatBursts,
    };
  },
});
